import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useProviderAuth } from '@/context/ProviderAuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Clock, CheckCircle, XCircle, ChevronRight } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const { provider, token } = useProviderAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API}/api/provider/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredAssignments = filterStatus === 'all'
    ? assignments
    : assignments.filter(a => a.status === filterStatus);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-stone-500">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-blue-600 font-medium">Total Leads</p>
                <p className="text-2xl sm:text-3xl font-bold text-blue-700">{assignments.length}</p>
              </div>
              <FileText size={28} className="text-blue-400 hidden sm:block" />
            </div>
          </Card>

          <Card className="p-3 sm:p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-yellow-600 font-medium">In Progress</p>
                <p className="text-2xl sm:text-3xl font-bold text-yellow-700">
                  {assignments.filter(a => a.status === 'in_progress').length}
                </p>
              </div>
              <Clock size={28} className="text-yellow-400 hidden sm:block" />
            </div>
          </Card>

          <Card className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-green-600 font-medium">Completed</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-700">
                  {assignments.filter(a => a.status === 'completed').length}
                </p>
              </div>
              <CheckCircle size={28} className="text-green-400 hidden sm:block" />
            </div>
          </Card>

          <Card className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-blue-600 font-medium">New</p>
                <p className="text-2xl sm:text-3xl font-bold text-blue-700">
                  {assignments.filter(a => a.status === 'assigned').length}
                </p>
              </div>
              <Clock size={28} className="text-blue-400 hidden sm:block" />
            </div>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            onClick={() => setFilterStatus('all')}
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            className={`whitespace-nowrap text-xs sm:text-sm ${filterStatus === 'all' ? 'bg-[#1A4D2E] hover:bg-[#143D24]' : ''}`}
          >
            All ({assignments.length})
          </Button>
          {Object.entries(statusConfig).map(([key, config]) => {
            const count = assignments.filter(a => a.status === key).length;
            return (
              <Button
                key={key}
                onClick={() => setFilterStatus(key)}
                variant={filterStatus === key ? 'default' : 'outline'}
                size="sm"
                className={`whitespace-nowrap text-xs sm:text-sm ${filterStatus === key ? 'bg-[#1A4D2E] hover:bg-[#143D24]' : ''}`}
              >
                {config.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Assignments List */}
        {filteredAssignments.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText size={64} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No Leads Found</h3>
            <p className="text-stone-600">
              {filterStatus === 'all'
                ? 'You have no assigned leads yet. Check back later!'
                : `No ${statusConfig[filterStatus].label.toLowerCase()} leads.`}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredAssignments.map((assignment) => {
              const StatusIcon = statusConfig[assignment.status]?.icon || Clock;
              return (
                <Card
                  key={assignment.id}
                  className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/provider/leads/${assignment.id}`)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                        <h3 className="text-base sm:text-xl font-bold text-stone-900 truncate">{assignment.visitor_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit ${statusConfig[assignment.status]?.color}`}>
                          <StatusIcon size={14} />
                          {statusConfig[assignment.status]?.label}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-stone-600 mb-3">
                        <div>
                          <p className="font-medium text-stone-700">Contact:</p>
                          <p className="truncate">{assignment.visitor_email}</p>
                          <p>{formatPhoneDisplay(assignment.visitor_phone)}</p>
                        </div>
                        <div>
                          <p className="font-medium text-stone-700">Property:</p>
                          <p className="truncate">{assignment.property_address || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-stone-500">
                        <span>Assigned by: {assignment.agent_name}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>{formatDate(assignment.created_at)}</span>
                      </div>
                    </div>

                    <ChevronRight size={24} className="text-stone-400 flex-shrink-0 mt-1" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
  );
};

export default ProviderDashboard;
